import { useEffect, useState, type ReactNode } from 'react';
import { Dialog, Menu } from '@headlessui/react';
import clsx from 'clsx';
import { Table, THead, Th, Td, Tr } from '../../../components/ui/Table';

/** A compact select, for filters and pickers inside rows. */
export const selectClass =
  'rounded-md border border-border-light bg-surface-primary px-2 py-1 text-body-xs text-text-secondary focus:outline-none focus:ring-1 focus:ring-accent';

/** "⋯" or a labelled button opening a short list of actions. Rare actions live here, not on the page. */
export function MoreMenu({ items, label = '⋯' }: { items: { label: string; onClick: () => void; danger?: boolean }[]; label?: ReactNode }) {
  return (
    <Menu as="div" className="relative">
      <Menu.Button className="rounded-md px-2 py-1 text-body-sm text-text-tertiary hover:bg-surface-secondary hover:text-text-primary">{label}</Menu.Button>
      <Menu.Items className="absolute right-0 z-40 mt-1 w-56 rounded-xl border border-border-light bg-surface-primary py-1 shadow-medium focus:outline-none">
        {items.map((item) => (
          <Menu.Item key={item.label}>
            {({ active }) => (
              <button
                onClick={item.onClick}
                className={clsx(
                  'block w-full px-3 py-1.5 text-left text-body-sm',
                  item.danger ? 'text-error' : 'text-text-secondary',
                  active && 'bg-surface-secondary',
                )}
              >
                {item.label}
              </button>
            )}
          </Menu.Item>
        ))}
      </Menu.Items>
    </Menu>
  );
}

/** A panel sliding in from the right, for everything about one row. */
export function SidePanel({ isOpen, onClose, title, children }: { isOpen: boolean; onClose: () => void; title: ReactNode; children: ReactNode }) {
  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-surface-inverse/20" />
      <div className="fixed inset-y-0 right-0 flex max-w-full pl-10">
        <Dialog.Panel className="flex w-screen max-w-md flex-col bg-surface-primary shadow-xl">
          <div className="flex items-start justify-between gap-3 border-b border-border-light px-5 py-4">
            <Dialog.Title as="div" className="min-w-0">{title}</Dialog.Title>
            <button onClick={onClose} className="text-body-sm text-text-tertiary hover:text-text-primary" aria-label="Close">✕</button>
          </div>
          <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">{children}</div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

/**
 * The most urgent blocker, with the others one click away: "266 people without a rep, 1 other issue".
 * `quiet` (a bucket that has not launched) is plain grey: open setup is the plan, not a fault.
 */
export function NextStep({ blocking, quiet = false }: { blocking: string[]; quiet?: boolean }) {
  const [all, setAll] = useState(false);
  if (blocking.length === 0) return null;
  // Two issues are named in full; only a longer list folds the tail behind a count.
  const named = all ? blocking.length : Math.min(blocking.length, 2);
  const rest = blocking.length - named;
  return (
    <span className={quiet ? 'text-body-xs text-text-tertiary' : 'text-body-xs text-warning-dark'}>
      {blocking.slice(0, named).join(', ')}
      {rest > 0 && (
        <>
          {', '}
          <button className="text-text-tertiary hover:underline" onClick={(e) => { e.stopPropagation(); setAll(true); }}>
            {rest} more
          </button>
        </>
      )}
    </span>
  );
}

/**
 * A table-shaped placeholder: the real column headers over pulsing rows, so the
 * page keeps its shape while the (slow) pilots endpoint answers. Says so after 12s.
 */
export function TableSkeleton({ columns, rows = 6, minWidth }: { columns: { label: string; align?: 'left' | 'right' }[]; rows?: number; minWidth?: number }) {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), 12_000);
    return () => clearTimeout(t);
  }, []);
  return (
    <div aria-busy="true" aria-live="polite">
      <Table minWidth={minWidth}>
        <THead>
          <tr>
            {columns.map((c) => <Th key={c.label} align={c.align}>{c.label}</Th>)}
          </tr>
        </THead>
        <tbody>
          {Array.from({ length: rows }).map((_, r) => (
            <Tr key={r}>
              {columns.map((c, i) => (
                <Td key={c.label} align={c.align}>
                  <div
                    className={clsx('h-4 animate-pulse rounded-sm bg-surface-tertiary', c.align === 'right' && 'ml-auto')}
                    style={{ width: i === 0 ? `${70 - (r % 3) * 12}%` : c.align === 'right' ? '2rem' : '60%' }}
                  />
                </Td>
              ))}
            </Tr>
          ))}
        </tbody>
      </Table>
      {slow && <p className="mt-3 text-body-xs text-text-tertiary">Still loading: this source is slower than usual.</p>}
    </div>
  );
}
